import React, { useState, useEffect, useRef } from 'react';
import { useVendorDirectory } from '../../utils/useVendorDirectory';

/**
 * Reusable Database Browser Component
 * 
 * Features:
 * - Search and filter inventory items
 * - Category, location, and vendor filtering
 * - Expandable item details
 * - Add items to lists/collections
 * - Configurable display options
 * - Custom styling support
 * 
 * @param {Object} props - Component props
 * @param {Array} props.inventoryData - Array of inventory items
 * @param {Array} props.currentItems - Currently selected items (to exclude from display)
 * @param {Function} props.onAddItem - Callback when item is added
 * @param {Function} props.onItemClick - Callback when item is clicked
 * @param {Object} props.config - Configuration options
 * @param {boolean} props.config.showSearch - Show search input (default: true)
 * @param {boolean} props.config.showFilters - Show filter dropdowns (default: true)
 * @param {boolean} props.config.showAddButton - Show add buttons (default: true)
 * @param {boolean} props.config.showItemCount - Show item count (default: true)
 * @param {boolean} props.config.expandableItems - Allow item expansion (default: true)
 * @param {string} props.config.addButtonText - Text for add button (default: "Add")
 * @param {Object} props.styling - Custom styling options
 */
const DatabaseBrowser = ({
  inventoryData = [],
  currentItems = [],
  onAddItem,
  onItemClick,
  onFilteredListChange,
  config = {},
  styling = {}
}) => {
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [checkedItems, setCheckedItems] = useState(new Set());

  const { resolveVendor, resolveUniqueVendors } = useVendorDirectory();

  // Configuration with defaults
  const {
    showSearch = true,
    showFilters = true,
    showAddButton = true,
    showItemCount = true,
    expandableItems = true,
    addButtonText = "Add",
    compactLayout = false,
    stickyFilters = false
  } = config;

  const configRef = useRef(config);
  configRef.current = config;

  const onFilteredListChangeRef = useRef(onFilteredListChange);
  onFilteredListChangeRef.current = onFilteredListChange;

  // Initialize filters and data
  useEffect(() => {
    if (inventoryData && inventoryData.length > 0) {
      // Extract unique values for filters; resolve vendor aliases to canonical names
      const uniqueCategories = [...new Set(inventoryData.map(item => item.category).filter(Boolean))].sort();
      const uniqueLocations = [...new Set(inventoryData.map(item => item.location).filter(Boolean))].sort();
      const uniqueVendors = resolveUniqueVendors(inventoryData);

      setCategories(uniqueCategories);
      setLocations(uniqueLocations);
      setVendors(uniqueVendors);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryData, resolveVendor]);

  // Apply filters
  useEffect(() => {
    let filtered = [...inventoryData];

    // Exclude current items — normalize to strings to avoid type-mismatch (number vs string)
    const currentItemIds = new Set(
      currentItems.flatMap(item =>
        [item.id, item.inventory_item_id].filter(Boolean).map(String)
      )
    );
    filtered = filtered.filter(
      item =>
        !currentItemIds.has(String(item.id)) &&
        !currentItemIds.has(String(item.inventory_item_id))
    );

    // Apply search filter; match canonical vendor name so aliases resolve
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        (item.description || '').toLowerCase().includes(query) ||
        (item.brand || '').toLowerCase().includes(query) ||
        (resolveVendor(item.vendor) || item.vendor || '').toLowerCase().includes(query) ||
        (item.category || '').toLowerCase().includes(query) ||
        (item.location || '').toLowerCase().includes(query)
      );
    }

    // Apply category filter
    if (categoryFilter) {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }

    // Apply location filter
    if (locationFilter) {
      filtered = filtered.filter(item => item.location === locationFilter);
    }

    // Apply vendor filter by canonical name
    if (vendorFilter) {
      filtered = filtered.filter(item => resolveVendor(item.vendor) === vendorFilter);
    }

    // Sort by description
    filtered.sort((a, b) => (a.description || '').localeCompare(b.description || ''));

    setFilteredItems(filtered);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryData, currentItems, searchQuery, categoryFilter, locationFilter, vendorFilter, resolveVendor]);

  useEffect(() => {
    const cb = onFilteredListChangeRef.current;
    if (typeof cb === 'function') {
      cb(filteredItems);
    }
  }, [filteredItems]);

  useEffect(() => {
    const cb = configRef.current.onFilterModelChange;
    if (typeof cb === 'function') {
      cb({
        searchQuery,
        categoryFilter,
        locationFilter,
        vendorFilter
      });
    }
  }, [searchQuery, categoryFilter, locationFilter, vendorFilter]);

  useEffect(() => {
    const cb = configRef.current.onFilteredCountChange;
    if (typeof cb === 'function') {
      cb(filteredItems.length);
    }
  }, [filteredItems]);

  useEffect(() => {
    setCheckedItems((prev) => {
      const visibleIds = new Set(filteredItems.map((item) => item.id));
      const next = new Set(Array.from(prev).filter((id) => visibleIds.has(id)));
      if (next.size !== prev.size && configRef.current.onCheckedItemsChange) {
        configRef.current.onCheckedItemsChange(Array.from(next));
      }
      return next;
    });
  }, [filteredItems]);

  // Handle item expansion
  const toggleItemExpansion = (itemId) => {
    if (!expandableItems) return;
    
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  // Handle add item
  const handleAddItem = (item) => {
    if (onAddItem) {
      onAddItem(item);
    }
  };

  // Handle item click
  const handleItemClick = (item, event) => {
    // Don't trigger if clicking on add button
    if (event.target.closest('.add-item-btn') || event.target.closest('.item-checkbox')) {
      return;
    }

    // If selectable mode is on, toggle checkbox instead of expanding
    if (config.selectable) {
      toggleCheck(item.id);
      return;
    }

    if (expandableItems) {
      toggleItemExpansion(item.id);
    }

    if (onItemClick) {
      onItemClick(item);
    }
  };

  const toggleCheck = (itemId) => {
    const newChecked = new Set(checkedItems);
    if (newChecked.has(itemId)) {
      newChecked.delete(itemId);
    } else {
      newChecked.add(itemId);
    }
    setCheckedItems(newChecked);
    // Let parent know
    if (config.onCheckedItemsChange) {
      config.onCheckedItemsChange(Array.from(newChecked));
    }
  };

  // Expose check/uncheck all to parent if a ref is provided
  if (config.browserApiRef) {
    config.browserApiRef.current = {
      checkAll: () => {
        const allIds = filteredItems.map(i => i.id);
        const newChecked = new Set(allIds);
        setCheckedItems(newChecked);
        if (config.onCheckedItemsChange) config.onCheckedItemsChange(allIds);
      },
      uncheckAll: () => {
        setCheckedItems(new Set());
        if (config.onCheckedItemsChange) config.onCheckedItemsChange([]);
      },
      getCheckedItemIds: () => Array.from(checkedItems),
      getFilteredItemIds: () => filteredItems.map((item) => item.id)
    };
  }

  // Default styling
  const containerStyle = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    ...styling.container
  };

  const headerStyle = {
    padding: compactLayout ? '0.5rem 0.75rem' : '1rem',
    borderBottom: '1px solid #dee2e6',
    backgroundColor: '#f8f9fa',
    ...(stickyFilters ? { position: 'sticky', top: 0, zIndex: 2 } : {}),
    ...styling.header
  };

  const filtersStyle = {
    display: 'flex',
    gap: compactLayout ? '0.35rem' : '0.5rem',
    marginTop: compactLayout ? '0.35rem' : '0.5rem',
    flexWrap: 'wrap',
    ...styling.filters
  };

  const itemsContainerStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: compactLayout ? '0.35rem' : '0.5rem',
    minHeight: 0,
    ...styling.itemsContainer
  };

  const itemCardStyle = {
    border: '1px solid #dee2e6',
    borderRadius: '0.25rem',
    marginBottom: compactLayout ? '0.25rem' : '0.5rem',
    backgroundColor: 'white',
    cursor: expandableItems ? 'pointer' : 'default',
    transition: 'box-shadow 0.15s ease-in-out',
    ...styling.itemCard
  };

  const itemHeaderStyle = {
    padding: compactLayout ? '0.35rem 0.5rem' : '0.75rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.5rem',
    ...styling.itemHeader
  };

  const itemDetailsStyle = {
    padding: '0 0.75rem 0.75rem',
    borderTop: '1px solid #f0f0f0',
    backgroundColor: '#f8f9fa',
    fontSize: '0.875rem',
    color: '#6c757d',
    ...styling.itemDetails
  };

  if (!inventoryData.length) {
    return (
      <div style={containerStyle}>
        <div className="text-center text-muted py-4">
          No inventory data available
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header with Search and Filters */}
      {(showSearch || showFilters || showItemCount) && (
        <div style={headerStyle}>
          {/* Search Input */}
          {showSearch && (
            <div className={compactLayout ? 'mb-1' : 'mb-2'}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styling.searchInput}
              />
            </div>
          )}

          {/* Filter Dropdowns */}
          {showFilters && (
            <div style={filtersStyle}>
              {/* Category Filter */}
              <select
                className="form-select form-select-sm"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{ minWidth: compactLayout ? '100px' : '120px', ...styling.filterSelect }}
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>

              {/* Location Filter */}
              <select
                className="form-select form-select-sm"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                style={{ minWidth: compactLayout ? '100px' : '120px', ...styling.filterSelect }}
              >
                <option value="">All Locations</option>
                {locations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>

              {/* Vendor Filter */}
              <select
                className="form-select form-select-sm"
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
                style={{ minWidth: compactLayout ? '100px' : '120px', ...styling.filterSelect }}
              >
                <option value="">All Vendors</option>
                {vendors.map(vendor => (
                  <option key={vendor} value={vendor}>{vendor}</option>
                ))}
              </select>
            </div>
          )}

          {/* Item Count */}
          {showItemCount && (
            <div className={compactLayout ? 'mt-1' : 'mt-2'}>
              <small className="text-muted" style={{ fontSize: compactLayout ? '0.7rem' : undefined }}>
                Showing {filteredItems.length} of {inventoryData.length} items
              </small>
            </div>
          )}
        </div>
      )}

      {/* Items List */}
      <div style={itemsContainerStyle}>
        {filteredItems.length === 0 ? (
          <div className="text-center text-muted py-4">
            {searchQuery || categoryFilter || locationFilter || vendorFilter
              ? 'No items match your filters'
              : 'No items available'
            }
          </div>
        ) : (
          filteredItems.map(item => (
            <div
              key={item.id}
              style={itemCardStyle}
              onClick={(e) => handleItemClick(item, e)}
              onMouseEnter={(e) => {
                if (expandableItems) {
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (expandableItems) {
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              {/* Item Header */}
              <div style={itemHeaderStyle}>
                {config.selectable && (
                  <div className="me-2">
                    <input
                      type="checkbox"
                      className="form-check-input item-checkbox"
                      style={{ cursor: 'pointer', width: '1.2rem', height: '1.2rem' }}
                      checked={checkedItems.has(item.id)}
                      onChange={() => toggleCheck(item.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: '600',
                    fontSize: compactLayout ? '0.8rem' : '0.9rem',
                    color: '#212529',
                    marginBottom: compactLayout ? '0.1rem' : '0.25rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {item.description || 'Unknown Item'}
                  </div>
                  <div style={{
                    fontSize: compactLayout ? '0.68rem' : '0.8rem',
                    color: '#6c757d',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {[item.location, resolveVendor(item.vendor)].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>

                {/* Add Button */}
                {showAddButton && onAddItem && (
                  <button
                    className="btn btn-sm btn-outline-primary add-item-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddItem(item);
                    }}
                    style={{
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem',
                      ...styling.addButton
                    }}
                  >
                    {addButtonText}
                  </button>
                )}
              </div>

              {/* Expanded Details */}
              {expandableItems && expandedItems.has(item.id) && (
                <div style={itemDetailsStyle}>
                  <div className="row">
                    {item.brand && (
                      <div className="col-6 mb-1">
                        <strong>Brand:</strong> {item.brand}
                      </div>
                    )}
                    {item.category && (
                      <div className="col-6 mb-1">
                        <strong>Category:</strong> {item.category}
                      </div>
                    )}
                    {item.unit && (
                      <div className="col-6 mb-1">
                        <strong>Unit:</strong> {item.unit}
                      </div>
                    )}
                    {item.pack_size && (
                      <div className="col-6 mb-1">
                        <strong>Pack Size:</strong> {item.pack_size}
                      </div>
                    )}
                    {item.manufacturer_code && (
                      <div className="col-12 mb-1">
                        <strong>Code:</strong> {item.manufacturer_code}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DatabaseBrowser;
